import React, {Fragment} from 'react';
import Select from 'react-select';
import {useModelFormEngine} from "../common/hooks/useModelFormEngine";
import {StringUtility} from "../common/StringUtility";
import {PillButton} from "../common/PillButton";
import {InvoiceDataUtility} from "../common/InvoiceDataUtility";
import {RadioOptionSet} from "../common/RadioOptionSet";
import {SelectUtility} from "../common/SelectUtility";
import {Constants} from "../common/Constants";
import {ConditionalRenderer} from "../common/ConditionalRenderer";
import {CheckBox} from "../common/CheckBox";
import {ApiUtility} from "../common/ApiUtility";
import {OverlayLoadingIndicator} from "../common/OverlayLoadingIndicator";
import {CreateStripeCustomer} from "../customers/CreateStripeCustomer";
import {CreateQboCustomer} from "../customers/CreateQboCustomer";
import {CustomerDataUtility} from "../common/CustomerDataUtility";
import {InvoiceLineWithServicesFormRow} from "./InvoiceLineWithServicesFormRow";
import {CheckBoxSet} from "../common/CheckBoxSet";
import {DateUtility} from "../common/DateUtility";
import DatePicker from "react-datepicker";
import _ from "lodash";

const getInvoiceLineFieldId = InvoiceDataUtility.getInvoiceLineFieldId

const stripeLinePresentationModeOptions = [
    {
        label: Constants.stripeFpInvoiceLinePresentation.totalsByServiceGroup,
        value: Constants.stripeFpInvoiceLinePresentation.totalsByServiceGroup
    },
    {
        label: Constants.stripeFpInvoiceLinePresentation.allLines,
        value: Constants.stripeFpInvoiceLinePresentation.allLines
    }
]

const emptyStripeInvoiceLine = {
    name: '',
    amount: '0'
}

const qboLinePresentationModeOptions = [
    {
        label: Constants.qboFpInvoiceLinePresentation.totalsByServiceType,
        value: Constants.qboFpInvoiceLinePresentation.totalsByServiceType
    },
    {
        label: Constants.qboFpInvoiceLinePresentation.allLines,
        value: Constants.qboFpInvoiceLinePresentation.allLines
    }
]

const emptyQboInvoiceLine = {
    invoice_line_type_id: Constants.qboInvoiceLineTypes.fpService,
    name: '',
    amount: '0'
}

export let PrepareInvoiceForBillingForm = function (props) {
    const invoice = _.clone(props.invoice) || {};
    const fpInvoice = _.clone(props.fpInvoice) || {};

    const stripeInvoiceItemsFull = (_.clone(props.fpInvoiceItems || [])).map(function (fpInvoiceItem) {
        return {
            name: InvoiceDataUtility.getFormattedFpInvoiceItemName(fpInvoiceItem),
            amount: fpInvoiceItem.total,
        }
    });
    const stripeInvoiceItemsByFpPhaseName =
        InvoiceDataUtility.fpInvoiceItemsGroupedByPhaseName(_.clone(props.fpInvoiceItems || []))

    const qboInvoiceItemsFull = (_.clone(fpInvoice.invoice_lines_for_form || [])).map(function (fpInvoiceLine) {
        return {
            invoice_line_type_id: Constants.qboInvoiceLineTypes.fpService,
            // QBO fails or applies weird formatting if you include colons in item names
            name: (fpInvoiceLine.name || '').replaceAll(':', ' -'),
            amount: parseFloat(fpInvoiceLine.amount || 0),
            fp_service_id: fpInvoiceLine.fp_service_id,
            fp_service_type_id: fpInvoiceLine.fp_service_type_id,
            ext_fp_service_id: fpInvoiceLine.ext_fp_service_id,
            fp_service_type_name: fpInvoiceLine.fp_service_type_name,
        }
    })
    const qboInvoiceItemsByFpServiceType =
        InvoiceDataUtility.fpInvoiceItemsGroupedByServiceType(_.clone(fpInvoice.invoice_lines_for_form || []))

    const getInitialModelState = function () {
        let initialStripeInvoiceDescription = fpInvoice.docket?.name || '';
        if (initialStripeInvoiceDescription && fpInvoice.description) {
            initialStripeInvoiceDescription += '\n\n' + fpInvoice.description
        }

        let proposedCustomer = CustomerDataUtility.proposedCustomerFromFpInvoice(fpInvoice)
        let fpInvoiceDate = DateUtility.fpInvoiceDateToJsDate(fpInvoice.invoicedate);

        return {
            customInvoiceNumber: '' + fpInvoice.invoicenumber,
            fpInvoiceNumber: '' + fpInvoice.invoicenumber,
            fpInvoiceId: fpInvoice.id,
            fpCompanyId: fpInvoice?.invoicecompany?.id,

            stripeInvoiceDescription: initialStripeInvoiceDescription,
            stripeDaysUntilDue: 30,
            stripeCustomerId: null,
            stripeLinePresentationModeId: Constants.stripeFpInvoiceLinePresentation.totalsByServiceGroup,
            stripeInvoiceItems: _.clone(stripeInvoiceItemsByFpPhaseName),
            customStripeInvoiceItems: false,

            qboInvoiceItems: _.clone(qboInvoiceItemsByFpServiceType),
            qboInvoiceDate: fpInvoiceDate || new Date(),
            qboCustomerId: null,
            qboLinePresentationModeId: Constants.qboFpInvoiceLinePresentation.totalsByServiceType,
            customQboInvoiceItems: false,

            showAddStripeCustomerModal: false,
            showAddQboCustomerModal: false,

            proposedStripeCustomer: _.clone(proposedCustomer),
            proposedQboCustomer: _.clone(proposedCustomer),

            supportedStripePaymentTypes: []
        };
    }

    const initialModelState = getInitialModelState();

    const fetchSupportingData = function () {
        let fpServiceOptions = [];
        let fpServiceTypeOptions = [];
        let supportedStripePaymentTypeOptions = [];
        let stripeCustomerOptions = [];
        let stripeCustomerOptionsFlat = [];
        let qboCustomerOptions = [];
        let qboCustomerOptionsFlat = [];

        return ApiUtility.getFpServicesList()
            .then(function (fpServices) {
                fpServiceOptions = (fpServices || []).map(function (fpService) {
                    return {
                        value: fpService['id'],
                        label: fpService['name'],
                        fpServiceTypeId: fpService.fp_service_type_id,
                        fpServiceTypeName: fpService.fp_service_type.name
                    }
                })
                return ApiUtility.getFpServiceTypesList()
            })
            .then(function (fpServiceTypesData) {
                fpServiceTypeOptions = SelectUtility.modelArrayToSelectOptions(fpServiceTypesData.fpServiceTypeList)
                return ApiUtility.getSupportedStripePaymentTypes()
            })
            .then(function (supportedStripePaymentTypes) {
                supportedStripePaymentTypeOptions = (supportedStripePaymentTypes || []).map(function (paymentType) {
                    return {
                        value: paymentType.ext_stripe_payment_type_key,
                        label: paymentType.label,
                        sublabel: paymentType.sublabel
                    }
                })
                return ApiUtility.getStripeCustomersList(fpInvoice?.invoicecompany?.id)
            })
            .then(function (stripeCustomers) {
                let stripeCustomerOptionsData = CustomerDataUtility.getStripeCustomerOptionsData(stripeCustomers)
                stripeCustomerOptions = stripeCustomerOptionsData.stripeCustomerOptions
                stripeCustomerOptionsFlat = stripeCustomerOptionsData.stripeCustomerOptionsFlat

                return ApiUtility.getQboCustomersList(fpInvoice?.invoicecompany?.id)
            })
            .then(function (qboCustomers) {
                let qboCustomerOptionsData = CustomerDataUtility.getQboCustomerOptionsData(qboCustomers)
                qboCustomerOptions = qboCustomerOptionsData.qboCustomerOptions
                qboCustomerOptionsFlat = qboCustomerOptionsData.qboCustomerOptionsFlat

                // set up post-fetch state change
                let stateChange = {
                    supportingData: {
                        fpServiceOptions: fpServiceOptions,
                        fpServiceTypeOptions: fpServiceTypeOptions,
                        stripeCustomerOptions: stripeCustomerOptions,
                        stripeCustomerOptionsFlat: stripeCustomerOptionsFlat,
                        qboCustomerOptions: qboCustomerOptions,
                        qboCustomerOptionsFlat: qboCustomerOptionsFlat,
                        qboInvoiceLineTypeOptions: [
                            {
                                label: Constants.qboInvoiceLineTypes.fpService,
                                value: Constants.qboInvoiceLineTypes.fpService
                            },
                            {
                                label: Constants.qboInvoiceLineTypes.custom,
                                value: Constants.qboInvoiceLineTypes.custom
                            },
                        ],
                    }
                }

                // auto-select Stripe customer if we find one that uses the FP invoice contact's email address
                if (formEngine.renderableData.proposedStripeCustomer?.email) {
                    let stripeCustomerOptionForEmail = _.find(stripeCustomerOptionsFlat, function (stripeCustomerOption) {
                        return (
                            formEngine.renderableData.proposedStripeCustomer?.email &&
                            stripeCustomerOption.email &&
                            formEngine.renderableData.proposedStripeCustomer?.email.toLowerCase() ===
                            stripeCustomerOption.email.toLowerCase()
                        )
                    })

                    if (stripeCustomerOptionForEmail) {
                        stateChange.stripeCustomerId = stripeCustomerOptionForEmail.value;
                        stateChange.proposedStripeCustomer = {};
                    } else {
                        let stripeCustomerOptionForCompanyName = _.find(stripeCustomerOptionsFlat, function (stripeCustomerOption) {
                            return (
                                formEngine.renderableData.proposedStripeCustomer?.companyName &&
                                stripeCustomerOption.companyName &&
                                formEngine.renderableData.proposedStripeCustomer?.companyName.trim().toLowerCase() ===
                                stripeCustomerOption.companyName.trim().toLowerCase()
                            )
                        })
                        if (stripeCustomerOptionForCompanyName) {
                            stateChange.stripeCustomerId = stripeCustomerOptionForCompanyName.value;
                            stateChange.proposedStripeCustomer = {};
                        }
                    }
                }

                // auto-select QBO customer if we find one that uses the FP invoice contact's email address
                if (formEngine.renderableData.proposedQboCustomer?.email) {
                    let qboCustomerOptionForEmail = _.find(qboCustomerOptionsFlat, function (qboCustomerOption) {
                        return (
                            formEngine.renderableData.proposedQboCustomer?.email &&
                            qboCustomerOption.email &&
                            formEngine.renderableData.proposedQboCustomer?.email.toLowerCase() ===
                            qboCustomerOption.email.toLowerCase()
                        )
                    })

                    if (qboCustomerOptionForEmail) {
                        stateChange.qboCustomerId = qboCustomerOptionForEmail.value;
                        stateChange.proposedQboCustomer = {};
                    } else {
                        let qboCustomerOptionForCompanyName = _.find(qboCustomerOptionsFlat, function (qboCustomerOption) {
                            return (
                                formEngine.renderableData.proposedQboCustomer?.companyName &&
                                qboCustomerOption.companyName &&
                                formEngine.renderableData.proposedQboCustomer?.companyName.trim().toLowerCase() ===
                                qboCustomerOption.companyName.trim().toLowerCase()
                            )
                        })

                        if (qboCustomerOptionForCompanyName) {
                            stateChange.qboCustomerId = qboCustomerOptionForCompanyName.value;
                            stateChange.proposedQboCustomer = {};
                        }
                    }
                }

                if (!formEngine.renderableData.setInitialSupportedStripePaymentTypes) {
                    // Only update supported payment methods on initial load. Auto-select supported payment methods depending on initial invoice total (only do this on initial load as well).
                    // This also disables Link if the credit card option gets deselected.
                    stateChange.supportingData.supportedStripePaymentTypeOptions = supportedStripePaymentTypeOptions
                    let supportedStripePaymentTypes = _.map(supportedStripePaymentTypeOptions, (paymentType) => {
                        return paymentType.value
                    })
                    let stripeLinesTotal = InvoiceDataUtility.getOneOffInvoiceLinesSum(
                        formEngine.renderableData.stripeInvoiceItems || []
                    )
                    if (stripeLinesTotal > Constants.oneOffInvoiceCreditCardTotalThreshold) {
                        supportedStripePaymentTypes = _.filter(supportedStripePaymentTypes, (paymentType) => {
                            return (
                                paymentType !== Constants.stripePaymentMethodTypeKeys.card &&
                                paymentType !== Constants.stripePaymentMethodTypeKeys.link
                            )
                        })
                        stateChange.supportingData.supportedStripePaymentTypeOptions = _.map(
                            supportedStripePaymentTypeOptions,
                            function (paymentType) {
                                if (paymentType.value === Constants.stripePaymentMethodTypeKeys.link) {
                                    paymentType.disabled = true;
                                }
                                return paymentType
                            }
                        )
                    }
                    stateChange.supportedStripePaymentTypes = supportedStripePaymentTypes
                    stateChange.setInitialSupportedStripePaymentTypes = true
                } else {
                    stateChange.supportingData.supportedStripePaymentTypeOptions = _.cloneDeep(formEngine.renderableData.supportingData?.supportedStripePaymentTypeOptions);
                }

                return Promise.resolve(stateChange);
            })
    }

    const formEngine = useModelFormEngine(
        initialModelState,
        invoice,
        fetchSupportingData,
        validateForm,
        formStateToSaveData,
        props.save
    );

    function validateForm(handleValidationResult) {
        let stripeLinesTotal = InvoiceDataUtility.getOneOffInvoiceLinesSum(formEngine.renderableData.stripeInvoiceItems)
        let qboLinesTotal = InvoiceDataUtility.getOneOffInvoiceLinesSum(formEngine.renderableData.qboInvoiceItems)

        return new Promise(function (resolve, reject) {
            let validationErrors = [];
            let invalidFields = [];

            Promise.resolve({})
                .then(function () {
                    if (!formEngine.renderableData.customInvoiceNumber) {
                        validationErrors.push('Please enter an invoice number.');
                        invalidFields.push('customInvoiceNumber');
                        return Promise.resolve({})
                    } else {
                        return ApiUtility.getIsInvoiceNumberAvailable(formEngine.renderableData.customInvoiceNumber)
                            .then(function (availabilityData) {
                                if (!availabilityData.isInvoiceNumberAvailable) {
                                    validationErrors.push(`${availabilityData.usedIn} is already using that invoice number. Please enter a different number.`);
                                    invalidFields.push('customInvoiceNumber');
                                }

                                return Promise.resolve({})
                            })
                            .catch(function (error) {
                                console.log("Error validating form: ", error);
                                reject("An error occurred while validating the form.")
                            })
                    }
                })
                .then(function () {
                    if (!formEngine.renderableData.stripeInvoiceDescription) {
                        validationErrors.push('Please enter a description.');
                        invalidFields.push('stripeInvoiceDescription');
                    } else if (formEngine.renderableData.stripeInvoiceDescription.length > Constants.maxInvoiceDescriptionLength) {
                        validationErrors.push(`Description can't be longer than ${Constants.maxInvoiceDescriptionLength} characters.`);
                        invalidFields.push('stripeInvoiceDescription');
                    }

                    if (
                        !formEngine.renderableData.stripeDaysUntilDue ||
                        !(parseInt(formEngine.renderableData.stripeDaysUntilDue) || 0) ||
                        formEngine.renderableData.stripeDaysUntilDue < 1
                    ) {
                        validationErrors.push('Please enter a Days Until Due of at least 1.');
                        invalidFields.push('stripeDaysUntilDue');
                    }

                    if (!formEngine.renderableData.stripeCustomerId) {
                        validationErrors.push('Please select a Stripe customer.');
                        invalidFields.push('stripeCustomerId');
                    }

                    if (!formEngine.renderableData.qboCustomerId) {
                        validationErrors.push('Please select a QBO customer.');
                        invalidFields.push('qboCustomerId');
                    }

                    if (!formEngine.renderableData.qboInvoiceDate) {
                        validationErrors.push('Please select a QBO invoice date.');
                        invalidFields.push('qboInvoiceDate');
                    }

                    if (stripeLinesTotal !== qboLinesTotal) {
                        validationErrors.push('Stripe and QBO invoice totals must match.');
                        invalidFields.push('stripeTotal');
                        invalidFields.push('qboTotal');
                    }

                    if (
                        !formEngine.renderableData.stripeInvoiceItems ||
                        formEngine.renderableData.stripeInvoiceItems.length === 0
                    ) {
                        validationErrors.push('Please add at least one line to the Stripe invoice.');
                        invalidFields.push('stripeInvoiceItems');
                    } else {
                        for (let s = 0; s < formEngine.renderableData.stripeInvoiceItems.length; s++) {
                            let stripeInvoiceLine = formEngine.renderableData.stripeInvoiceItems[s];

                            if (!stripeInvoiceLine.name) {
                                validationErrors.push(`Please enter a label for Stripe invoice line ${s + 1}.`);
                                invalidFields.push(getInvoiceLineFieldId('stripeInvoiceItems', 'name', s));
                            }
                        }
                    }

                    if (
                        !formEngine.renderableData.qboInvoiceItems ||
                        formEngine.renderableData.qboInvoiceItems.length === 0
                    ) {
                        validationErrors.push('Please add at least one line to the QBO invoice.');
                        invalidFields.push('qboInvoiceItems');
                    } else {
                        for (let q = 0; q < formEngine.renderableData.qboInvoiceItems.length; q++) {
                            let qboInvoiceLine = formEngine.renderableData.qboInvoiceItems[q];

                            if (!qboInvoiceLine.invoice_line_type_id) {
                                validationErrors.push(`Please select a line type for QBO invoice line ${q + 1}.`);
                                invalidFields.push(getInvoiceLineFieldId('qboInvoiceItems', 'invoice_line_type_id', q));
                            }

                            if (qboInvoiceLine.invoice_line_type_id === Constants.qboInvoiceLineTypes.custom) {
                                if (!qboInvoiceLine.fp_service_type_id) {
                                    validationErrors.push(`Please select an FP service type for QBO invoice line ${q + 1}.`);
                                    invalidFields.push(getInvoiceLineFieldId('qboInvoiceItems', 'fp_service_type_id', q));
                                }
                            } else {
                                if (!qboInvoiceLine.fp_service_id) {
                                    validationErrors.push(`Please select an FP service for QBO invoice line ${q + 1}.`);
                                    invalidFields.push(getInvoiceLineFieldId('qboInvoiceItems', 'fp_service_id', q));
                                }
                            }

                            if (!qboInvoiceLine.name) {
                                validationErrors.push(`Please enter a label for QBO invoice line ${q + 1}.`);
                                invalidFields.push(getInvoiceLineFieldId('qboInvoiceItems', 'name', q));
                            } else if (qboInvoiceLine.name.indexOf(':') > -1) {
                                validationErrors.push(`QBO invoice lines can't contain colons (:). Please remove any colons from line ${q + 1}.`);
                                invalidFields.push(getInvoiceLineFieldId('qboInvoiceItems', 'name', q));
                            }
                        }
                    }

                    return handleValidationResult(validationErrors, invalidFields, resolve);
                });
        });
    }

    function formStateToSaveData(formStateToTransform) {
        let saveData = _.clone(formStateToTransform);
        saveData.supportingData = null;
        saveData.billedInvoiceTotal = _.sumBy(saveData.stripeInvoiceItems, function (stripeInvoiceItem) {
            return parseFloat(stripeInvoiceItem.amount)
        })
        return _.clone(saveData);
    }

    function handleSelectChange(fieldName, option) {
        formEngine.updateAndRenderUnsavedData({[fieldName]: option.value});
    }

    function handleDateChange(fieldName, date) {
        formEngine.updateAndRenderUnsavedData({[fieldName]: date});
    }

    function handleRadioButtonChange(fieldName, option) {
        if (formEngine.renderableData[fieldName] !== option.value) {
            let stateChange = {
                [fieldName]: option.value
            }
            if (fieldName === 'stripeLinePresentationModeId') {
                if (option.value === Constants.stripeFpInvoiceLinePresentation.allLines) {
                    stateChange.stripeInvoiceItems = _.clone(stripeInvoiceItemsFull)
                } else {
                    stateChange.stripeInvoiceItems = _.clone(stripeInvoiceItemsByFpPhaseName)
                }
            } else if (fieldName === 'qboLinePresentationModeId') {
                if (option.value === Constants.qboFpInvoiceLinePresentation.allLines) {
                    stateChange.qboInvoiceItems = _.clone(qboInvoiceItemsFull)
                } else {
                    stateChange.qboInvoiceItems = _.clone(qboInvoiceItemsByFpServiceType)
                }
            }
            formEngine.updateAndRenderUnsavedData(stateChange);
        }
    }

    function handleCheckboxChange(fieldName, value) {
        let stateChange = {[fieldName]: value};
        if (fieldName === 'customStripeInvoiceItems' && value === false) {
            if (formEngine.renderableData.stripeLinePresentationModeId === Constants.stripeFpInvoiceLinePresentation.allLines) {
                stateChange.stripeInvoiceItems = _.clone(stripeInvoiceItemsFull)
            } else {
                stateChange.stripeInvoiceItems = _.clone(stripeInvoiceItemsByFpPhaseName)
            }
        } else if (fieldName === 'customQboInvoiceItems' && value === false) {
            if (formEngine.renderableData.qboLinePresentationModeId === Constants.qboFpInvoiceLinePresentation.allLines) {
                stateChange.qboInvoiceItems = _.clone(qboInvoiceItemsFull)
            } else {
                stateChange.qboInvoiceItems = _.clone(qboInvoiceItemsByFpServiceType)
            }
        }
        formEngine.updateAndRenderUnsavedData(stateChange)
    }

    function handleInvoiceLineSelectChange(linesFieldName, fieldName, option, index) {
        let invoiceLinesUpdate = _.cloneDeep(formEngine.renderableData[linesFieldName]);
        invoiceLinesUpdate[index][fieldName] = option.value
        if (fieldName === 'invoice_line_type_id') {
            if (_.cloneDeep(formEngine.renderableData[linesFieldName])[index][fieldName] !== option.value) {
                invoiceLinesUpdate[index].fp_service_id = null
                invoiceLinesUpdate[index].fp_service_type_id = null
                invoiceLinesUpdate[index].fp_service_type_name = null
            }
        }
        if (fieldName === 'fp_service_id') {
            invoiceLinesUpdate[index].fp_service_type_id = option.fpServiceTypeId
            invoiceLinesUpdate[index].fp_service_type_name = option.fpServiceTypeName
            invoiceLinesUpdate[index].name = option.label
        }
        if (fieldName === 'fp_service_type_id') {
            invoiceLinesUpdate[index].fp_service_type_name = option.label
        }
        formEngine.updateAndRenderUnsavedData({[linesFieldName]: invoiceLinesUpdate});
    }

    function handleInvoiceLineTextFieldChange(linesFieldName, fieldName, event, index) {
        if (event && event.target) {
            let invoiceLinesUpdate = _.clone(formEngine.renderableData[linesFieldName]);
            invoiceLinesUpdate[index][fieldName] = event.target.value
            formEngine.updateAndRenderUnsavedData({[linesFieldName]: invoiceLinesUpdate});
        }
    }

    function handleSupportedStripePaymentTypesChange(updatedSupportedStripePaymentTypes) {
        // This deselects Link if the Credit Card option gets deselected. This also toggles whether or not the Link
        // option is disabled depending on if the CC option is selected.
        let disableLinkOption = !updatedSupportedStripePaymentTypes.includes(Constants.stripePaymentMethodTypeKeys.card)
        if (disableLinkOption) {
            updatedSupportedStripePaymentTypes = _.filter(updatedSupportedStripePaymentTypes, (paymentType) => {
                return paymentType !== Constants.stripePaymentMethodTypeKeys.link
            })
        }
        let stateChange = {
            supportingData: _.cloneDeep(formEngine.renderableData.supportingData),
            supportedStripePaymentTypes: updatedSupportedStripePaymentTypes
        }
        stateChange.supportingData.supportedStripePaymentTypeOptions = _.map(
            stateChange.supportingData.supportedStripePaymentTypeOptions,
            function (paymentType) {
                if (paymentType.value === Constants.stripePaymentMethodTypeKeys.link) {
                    paymentType.disabled = disableLinkOption;
                }
                return paymentType
            }
        )
        formEngine.updateAndRenderUnsavedData(stateChange);
    }

    function forceInvoiceLineValueToFloat(linesFieldName, fieldName, index) {
        let invoiceLinesUpdate = _.clone(formEngine.renderableData[linesFieldName]);
        invoiceLinesUpdate[index][fieldName] = (parseFloat(invoiceLinesUpdate[index][fieldName]) || 0.0).toFixed(2);
        formEngine.updateAndRenderUnsavedData({[linesFieldName]: invoiceLinesUpdate});
    }

    function deleteInvoiceLineItem(linesFieldName, index) {
        let invoiceLinesUpdate = _.clone(formEngine.renderableData[linesFieldName]);
        invoiceLinesUpdate.splice(index, 1);
        formEngine.updateAndRenderUnsavedData({[linesFieldName]: invoiceLinesUpdate});
    }

    function addInvoiceLineItem(linesFieldName, emptyInvoiceLine) {
        let invoiceLinesUpdate = _.clone(formEngine.renderableData[linesFieldName]);
        invoiceLinesUpdate.push(_.clone(emptyInvoiceLine));
        formEngine.updateAndRenderUnsavedData({[linesFieldName]: invoiceLinesUpdate});
    }

    function onClickAddStripeCustomer() {
        formEngine.renderableData.updateAndRender({showAddStripeCustomerModal: true})
    }

    function closeAddStripeCustomerModal() {
        formEngine.renderableData.updateAndRender({showAddStripeCustomerModal: false})
    }

    function afterAddStripeCustomer(customerId) {
        closeAddStripeCustomerModal()
        formEngine.updateAndRenderUnsavedData({stripeCustomerId: customerId})
        formEngine.refreshSupportingData()
    }

    function onClickAddQboCustomer() {
        formEngine.renderableData.updateAndRender({showAddQboCustomerModal: true})
    }

    function closeAddQboCustomerModal() {
        formEngine.renderableData.updateAndRender({showAddQboCustomerModal: false})
    }

    function afterAddQboCustomer(customerId) {
        closeAddQboCustomerModal()
        formEngine.updateAndRenderUnsavedData({qboCustomerId: customerId})
        formEngine.refreshSupportingData()
    }

    function getStripeInvoiceItemsDisplay() {
        let stripeInvoiceItemsDisplay = []
        formEngine.renderableData.stripeInvoiceItems.forEach(function (invoiceLineItem, index) {
            stripeInvoiceItemsDisplay.push(
                <StripeInvoiceLineFormRow
                    key={index}
                    index={index}
                    sequenceNumber={index + 1}
                    invoiceLine={invoiceLineItem}
                    handleInvoiceLineTextFieldChange={
                        (fieldName, event) => handleInvoiceLineTextFieldChange('stripeInvoiceItems', fieldName, event, index)
                    }
                    forceInvoiceLineValueToFloat={
                        (fieldName) => forceInvoiceLineValueToFloat('stripeInvoiceItems', fieldName, index)
                    }
                    getFormFieldClasses={formEngine.getFormFieldClasses}
                    deleteInvoiceLineItem={() => deleteInvoiceLineItem('stripeInvoiceItems', index)}
                    allowEdits={formEngine.renderableData.customStripeInvoiceItems}
                />
            )
        })
        if (stripeInvoiceItemsDisplay.length === 0) {
            stripeInvoiceItemsDisplay = (
                <div className="empty-invoice-line-list-message">No invoice lines found.</div>
            )
        }
        return stripeInvoiceItemsDisplay;
    }

    function getQboInvoiceHeaderDisplay() {
        return (
            <div className="common-form-list-header">
                <div className="common-form-list-header-content">
                    <div className="common-form-list-row-cell number">#</div>
                    <ConditionalRenderer
                        if={formEngine.renderableData.customQboInvoiceItems}
                        renderer={() => (<div className="common-form-list-row-cell type">Line Type</div>)}
                    />
                    <div className="common-form-list-row-cell fp-service">FP Service</div>
                    <div className="common-form-list-row-cell name">Label</div>
                    <div className="common-form-list-row-cell fp-service-type">FP Service Type</div>
                    <div className="common-form-list-row-cell amount">Amount</div>
                    <ConditionalRenderer
                        if={formEngine.renderableData.customQboInvoiceItems}
                        renderer={() => (<div className="common-form-list-row-cell delete-button"></div>)}
                    />
                </div>
            </div>
        )
    }

    function getQboInvoiceItemsDisplay() {
        let qboInvoiceItemsDisplay = []
        const supportingData = formEngine.renderableData.supportingData
        formEngine.renderableData.qboInvoiceItems.forEach(function (invoiceLineItem, index) {
            qboInvoiceItemsDisplay.push(
                <InvoiceLineWithServicesFormRow
                    key={index}
                    index={index}
                    sequenceNumber={index + 1}
                    invoiceLine={invoiceLineItem}
                    invoiceLinesFieldId={'qboInvoiceItems'}
                    qboInvoiceLineTypeOptions={supportingData.qboInvoiceLineTypeOptions}
                    fpServiceOptions={supportingData.fpServiceOptions}
                    fpServiceTypeOptions={supportingData.fpServiceTypeOptions}
                    handleInvoiceLineSelectChange={
                        (fieldName, option) => handleInvoiceLineSelectChange('qboInvoiceItems', fieldName, option, index)
                    }
                    handleInvoiceLineTextFieldChange={
                        (fieldName, event) => handleInvoiceLineTextFieldChange('qboInvoiceItems', fieldName, event, index)
                    }
                    forceInvoiceLineValueToFloat={
                        (fieldName) => forceInvoiceLineValueToFloat('qboInvoiceItems', fieldName, index)
                    }
                    getFormFieldClasses={formEngine.getFormFieldClasses}
                    deleteInvoiceLineItem={() => deleteInvoiceLineItem('qboInvoiceItems', index)}
                    allowEdits={formEngine.renderableData.customQboInvoiceItems}
                />
            )
        })
        if (qboInvoiceItemsDisplay.length === 0) {
            qboInvoiceItemsDisplay = (
                <div className="empty-invoice-line-list-message">No invoice lines found.</div>
            )
        }
        return qboInvoiceItemsDisplay;
    }

    let stripeLinesTotal = InvoiceDataUtility.getOneOffInvoiceLinesSum(formEngine.renderableData.stripeInvoiceItems)
    let qboLinesTotal = InvoiceDataUtility.getOneOffInvoiceLinesSum(formEngine.renderableData.qboInvoiceItems)
    let supportingData = formEngine.renderableData.supportingData
    let descriptionLength = ('' + formEngine.renderableData.stripeInvoiceDescription || '').length;
    let lengthIndicatorClass = `length-indicator ${descriptionLength > Constants.maxInvoiceDescriptionLength ? 'invalid' : ''}`

    return (
        <Fragment>
            <div className="common-form invoice-billing-prep-form">
                <ConditionalRenderer
                    if={formEngine.renderableData.showOverlayLoadingIndicator}
                    renderer={() => (
                        <OverlayLoadingIndicator overlayClass="invoice-billing-loading-indicator-overlay"/>
                    )}
                />
                <ConditionalRenderer if={supportingData} renderer={() => (
                    <div className="common-form-body">
                        <div className="common-form-body-row">
                            <div
                                className={formEngine.getFormFieldClasses(
                                    "common-form-field custom-invoice-number-field",
                                    "customInvoiceNumber"
                                )}
                            >
                                <div className="common-form-field-label">
                                    <div className="common-form-field-label-content">
                                        <div className="common-form-field-main-label">
                                            Invoice Number
                                        </div>
                                        <div className="common-form-field-sub-label">
                                            (used in Stripe and QBO)
                                        </div>
                                    </div>
                                </div>
                                <div className="common-form-field-input-container">
                                    <input
                                        id="customInvoiceNumber"
                                        type="text"
                                        className="common-form-input"
                                        value={formEngine.renderableData.customInvoiceNumber}
                                        onChange={(event) => formEngine.handleTextFieldChange("customInvoiceNumber", event)}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="form-section invoice-billing-prep-form-stripe">
                            <div className="form-section-label">Stripe Invoice</div>
                            <div className="form-section-body">
                                <div className="common-form-body-row">
                                    <div
                                        className={formEngine.getFormFieldClasses(
                                            "common-form-field",
                                            "stripeInvoiceDescription"
                                        )}
                                    >
                                        <div className="common-form-field-label">
                                            Description (Memo)
                                        </div>
                                        <div className="common-form-field-input-container">
                                            <div className="common-form-field-input-container-content">
                                                <textarea
                                                    id="stripeInvoiceDescription"
                                                    className="common-form-textarea"
                                                    value={formEngine.renderableData.stripeInvoiceDescription}
                                                    onChange={(event) => formEngine.handleTextFieldChange(
                                                        "stripeInvoiceDescription",
                                                        event
                                                    )}
                                                    maxLength={Constants.maxInvoiceDescriptionLength}
                                                />
                                                <div className={lengthIndicatorClass}>
                                                    {`${descriptionLength} / ${Constants.maxInvoiceDescriptionLength}`}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="common-form-body-row">
                                    <div
                                        className={formEngine.getFormFieldClasses(
                                            "common-form-field",
                                            "stripeDaysUntilDue"
                                        )}
                                    >
                                        <div className="common-form-field-label">
                                            Days Until Due
                                        </div>
                                        <div className="common-form-field-input-container">
                                            <input
                                                id="stripeDaysUntilDue"
                                                type="text"
                                                className="common-form-input"
                                                value={formEngine.renderableData.stripeDaysUntilDue}
                                                onChange={(event) => formEngine.handleTextFieldChange("stripeDaysUntilDue", event)}
                                                onBlur={() => formEngine.forceValueToNumeric('stripeDaysUntilDue')}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="common-form-body-row">
                                    <div
                                        className={formEngine.getFormFieldClasses(
                                            "common-form-field",
                                            "stripeCustomerId"
                                        )}
                                    >
                                        <div className="common-form-field-label">
                                            Customer
                                        </div>
                                        <div className="common-form-field-input-container">
                                            <Select
                                                className="common-form-select stripe-customer-select"
                                                classNamePrefix="common-form-select"
                                                value={SelectUtility.selectOptionForId(
                                                    formEngine.renderableData.stripeCustomerId,
                                                    formEngine.renderableData.supportingData.stripeCustomerOptionsFlat
                                                )}
                                                onChange={(option) => handleSelectChange(
                                                    'stripeCustomerId',
                                                    option
                                                )}
                                                options={formEngine.renderableData.supportingData.stripeCustomerOptions}
                                            />
                                            <div className="common-form-select-add-option-container">
                                                <PillButton
                                                    onClick={onClickAddStripeCustomer}
                                                    leftIconSrc={"/static/images/plus-textcolor.svg"}
                                                    containerClasses="add-button-container"
                                                    buttonClasses="add-button add-stripe-customer-button"
                                                    title="Add Stripe Customer"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="common-form-body-row">
                                    <div
                                        className={formEngine.getFormFieldClasses(
                                            "common-form-field checkbox-set-field",
                                            "supportedStripePaymentTypes"
                                        )}
                                    >
                                        <div className="common-form-field-label">
                                            Supported Payment Methods
                                        </div>
                                        <div className="common-form-field-input-container">
                                            <CheckBoxSet
                                                options={supportingData.supportedStripePaymentTypeOptions}
                                                onChange={handleSupportedStripePaymentTypesChange}
                                                values={formEngine.renderableData.supportedStripePaymentTypes}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <ConditionalRenderer
                                    if={(formEngine.renderableData.supportedStripePaymentTypes || []).length === 0}
                                    renderer={() => (
                                        <div className="common-form-body-row payment-methods-warning">
                                            <div className="common-form-field">
                                                <div className="common-form-field-label">
                                                </div>
                                                <div className="common-form-field-input-container red-warning-text">
                                                    Note: Selecting no supported payment methods will cause Stripe to
                                                    allow the default supported payment methods defined in the Stripe
                                                    admin; it will NOT disable all types of online payments.
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                />

                                <div
                                    className={
                                        "common-form-list invoice-lines-list stripe-invoice-lines-list " +
                                        (formEngine.renderableData.customStripeInvoiceItems ? '' : 'read-only')
                                    }
                                >
                                    <div className="form-sub-section">
                                        <div className="form-sub-section-label">
                                            Line Items
                                        </div>
                                        <div className="form-sub-section-body">
                                            <div className="common-form-body-row">
                                                <div className="common-form-body-row-horizontal">
                                                    <div className="common-form-body-row-horizontal-left">
                                                        <div className={formEngine.getFormFieldClasses(
                                                            "common-form-field common-radio-option-set-form-field",
                                                            "stripeLinePresentationModeId"
                                                        )}>
                                                            <div className="common-form-field-label">
                                                                Line Presentation
                                                            </div>
                                                            <div className="common-form-field-input-container">
                                                                <RadioOptionSet
                                                                    wrapperClass="stripe-line-presentation-option-set"
                                                                    options={stripeLinePresentationModeOptions}
                                                                    selectedOption={
                                                                        SelectUtility.selectOptionForId(
                                                                            formEngine.renderableData.stripeLinePresentationModeId,
                                                                            stripeLinePresentationModeOptions
                                                                        )
                                                                    }
                                                                    onSelectRadioOption={handleRadioButtonChange}
                                                                    fieldName="stripeLinePresentationModeId"
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="common-form-body-row-horizontal-right checkbox-row">
                                                        <div className="common-form-field">
                                                            <div className="common-form-field-label">
                                                                Customize Lines?
                                                            </div>
                                                            <div className="common-form-field-input-container">
                                                                <CheckBox
                                                                    id="customizeStripeInvoiceItems"
                                                                    checked={formEngine.renderableData.customStripeInvoiceItems}
                                                                    onChange={(checked) => handleCheckboxChange(
                                                                        'customStripeInvoiceItems',
                                                                        checked
                                                                    )}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="common-form-list-header">
                                        <div className="common-form-list-header-content">
                                            <div className="common-form-list-row-cell number">#</div>
                                            <div className="common-form-list-row-cell name">Label</div>
                                            <div className="common-form-list-row-cell amount">Amount</div>
                                            <ConditionalRenderer
                                                if={formEngine.renderableData.customStripeInvoiceItems}
                                                renderer={() => (
                                                    <div
                                                        className="common-form-list-row-cell delete-button"></div>
                                                )}
                                            />
                                        </div>
                                    </div>
                                    <div className="common-form-list-body">
                                        {getStripeInvoiceItemsDisplay()}
                                    </div>
                                    <div
                                        className={formEngine.getFormFieldClasses(
                                            "prep-invoice-line-items-total",
                                            "stripeTotal"
                                        )}
                                    >
                                        <div className="line-amounts-total">
                                            {`Total: ${StringUtility.stringNumberToDisplayCurrency('' + stripeLinesTotal)}`}
                                        </div>
                                        <ConditionalRenderer
                                            if={
                                                formEngine.renderableData.customStripeInvoiceItems &&
                                                stripeLinesTotal !== parseFloat(fpInvoice.total)
                                            }
                                            renderer={() => (
                                                <div className="original-fp-invoice-total">
                                                    {`Original FP Invoice Total: ${StringUtility.stringNumberToDisplayCurrency(fpInvoice.total)}`}
                                                </div>
                                            )}
                                        />
                                    </div>
                                    <ConditionalRenderer
                                        if={formEngine.renderableData.customStripeInvoiceItems}
                                        renderer={() => (
                                            <PillButton
                                                onClick={() => addInvoiceLineItem('stripeInvoiceItems', emptyStripeInvoiceLine)}
                                                leftIconSrc={"/static/images/plus-textcolor.svg"}
                                                containerClasses="add-button-container add-invoice-line-button-container"
                                                buttonClasses="add-button add-invoice-line-button add-stripe-invoice-line-button"
                                                buttonText={"ADD LINE"}
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="form-section invoice-billing-prep-form-qbo">
                            <div className="form-section-label">QBO Invoice</div>
                            <div className="form-section-body">
                                <div className="common-form-body-row">
                                    <div
                                        className={formEngine.getFormFieldClasses(
                                            "common-form-field",
                                            "qboCustomerId"
                                        )}
                                    >
                                        <div className="common-form-field-label">
                                            Customer
                                        </div>
                                        <div className="common-form-field-input-container">
                                            <Select
                                                className="common-form-select qbo-customer-select"
                                                classNamePrefix="common-form-select"
                                                value={SelectUtility.selectOptionForId(
                                                    formEngine.renderableData.qboCustomerId,
                                                    formEngine.renderableData.supportingData.qboCustomerOptionsFlat
                                                )}
                                                onChange={(option) => handleSelectChange(
                                                    'qboCustomerId',
                                                    option
                                                )}
                                                options={formEngine.renderableData.supportingData.qboCustomerOptions}
                                            />
                                            <div className="common-form-select-add-option-container">
                                                <PillButton
                                                    onClick={onClickAddQboCustomer}
                                                    leftIconSrc={"/static/images/plus-textcolor.svg"}
                                                    containerClasses="add-button-container"
                                                    buttonClasses="add-button add-qbo-customer-button"
                                                    title="Add QBO Customer"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="common-form-body-row">
                                    <div
                                        className={formEngine.getFormFieldClasses(
                                            "common-form-field qbo-invoice-date",
                                            "qboInvoiceDate"
                                        )}
                                    >
                                        <div className="common-form-field-label">
                                            Invoice Date
                                        </div>
                                        <div className="common-form-field-input-container">
                                            <DatePicker
                                                selected={formEngine.renderableData.qboInvoiceDate}
                                                onChange={(date) => handleDateChange('qboInvoiceDate', date)}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div
                                    className={
                                        "common-form-list invoice-lines-list qbo-invoice-lines-list " +
                                        (formEngine.renderableData.customQboInvoiceItems ? '' : 'read-only')
                                    }
                                >
                                    <div className="form-sub-section">
                                        <div className="form-sub-section-label">
                                            Line Items
                                        </div>
                                        <div className="form-sub-section-body">
                                            <div className="common-form-body-row">
                                                <div className="common-form-body-row-horizontal">
                                                    <div className="common-form-body-row-horizontal-left">
                                                        <div className={formEngine.getFormFieldClasses(
                                                            "common-form-field common-radio-option-set-form-field",
                                                            "qboLinePresentationModeId"
                                                        )}>
                                                            <div className="common-form-field-label">
                                                                Line Presentation
                                                            </div>
                                                            <div className="common-form-field-input-container">
                                                                <RadioOptionSet
                                                                    wrapperClass="qbo-line-presentation-option-set"
                                                                    options={qboLinePresentationModeOptions}
                                                                    selectedOption={
                                                                        SelectUtility.selectOptionForId(
                                                                            formEngine.renderableData.qboLinePresentationModeId,
                                                                            qboLinePresentationModeOptions
                                                                        )
                                                                    }
                                                                    onSelectRadioOption={handleRadioButtonChange}
                                                                    fieldName="qboLinePresentationModeId"
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="common-form-body-row-horizontal-right checkbox-row">
                                                        <div className="common-form-field">
                                                            <div className="common-form-field-label">
                                                                Customize Lines?
                                                            </div>
                                                            <div className="common-form-field-input-container">
                                                                <CheckBox
                                                                    id="customizeQboInvoiceItems"
                                                                    checked={formEngine.renderableData.customQboInvoiceItems}
                                                                    onChange={(checked) => handleCheckboxChange(
                                                                        'customQboInvoiceItems',
                                                                        checked
                                                                    )}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {getQboInvoiceHeaderDisplay()}
                                    <div className="common-form-list-body">
                                        {getQboInvoiceItemsDisplay()}
                                    </div>
                                    <div
                                        className={formEngine.getFormFieldClasses(
                                            "prep-invoice-line-items-total",
                                            "qboTotal"
                                        )}
                                    >
                                        <div className="line-amounts-total">
                                            {`Total: ${StringUtility.stringNumberToDisplayCurrency('' + qboLinesTotal)}`}
                                        </div>
                                        <ConditionalRenderer
                                            if={
                                                formEngine.renderableData.customQboInvoiceItems &&
                                                qboLinesTotal !== parseFloat(fpInvoice.total)
                                            }
                                            renderer={() => (
                                                <div className="original-fp-invoice-total">
                                                    {`Original FP Invoice Total: ${StringUtility.stringNumberToDisplayCurrency(fpInvoice.total)}`}
                                                </div>
                                            )}
                                        />
                                    </div>
                                    <ConditionalRenderer
                                        if={formEngine.renderableData.customQboInvoiceItems}
                                        renderer={() => (
                                            <PillButton
                                                onClick={() => addInvoiceLineItem('qboInvoiceItems', emptyQboInvoiceLine)}
                                                leftIconSrc={"/static/images/plus-textcolor.svg"}
                                                containerClasses="add-button-container add-invoice-line-button-container"
                                                buttonClasses="add-button add-invoice-line-button add-qbo-invoice-line-button"
                                                buttonText={"ADD LINE"}
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="common-form-body-row invoice-billing-prep-form-options-row">
                            <div className="invoice-billing-prep-form-submit-description">
                                {"Submitting this invoice will create it in Stripe and QBO. Stripe will send the invoice to the customer after a one-hour grace period."}
                            </div>
                            <ConditionalRenderer
                                if={
                                    (formEngine.renderableData.supportedStripePaymentTypes || []).includes(Constants.stripePaymentMethodTypeKeys.card) &&
                                    stripeLinesTotal > Constants.oneOffInvoiceCreditCardTotalThreshold
                                }
                                renderer={() => (
                                    <div className="invoice-billing-prep-form-submit-description credit-card-warning">
                                        {"WARNING: This invoice's total is over $3000, but it allows credit card payments!"}
                                    </div>
                                )}
                            />
                            <div className="common-form-options">
                                <PillButton
                                    onClick={formEngine.onClickSave}
                                    containerClasses="common-form-button-container"
                                    buttonClasses="common-form-button save-button"
                                    buttonText={"SUBMIT"}
                                />
                            </div>
                        </div>
                    </div>
                )}/>
            </div>
            {(() => {
                if (formEngine.renderableData.showAddStripeCustomerModal) {
                    return (
                        <CreateStripeCustomer
                            customer={formEngine.renderableData.proposedStripeCustomer}
                            cancel={closeAddStripeCustomerModal}
                            afterSuccessfulSave={afterAddStripeCustomer}
                        />
                    )
                } else if (formEngine.renderableData.showAddQboCustomerModal) {
                    return (
                        <CreateQboCustomer
                            customer={formEngine.renderableData.proposedQboCustomer}
                            cancel={closeAddQboCustomerModal}
                            afterSuccessfulSave={afterAddQboCustomer}
                        />
                    )
                }
            })()}
        </Fragment>
    );
};

let StripeInvoiceLineFormRow = (props) => {
    let nameContent;
    let amountContent;

    if (props.allowEdits) {
        nameContent = (
            <input
                type="text"
                className="common-form-input"
                value={props.invoiceLine.name}
                onChange={(event) => props.handleInvoiceLineTextFieldChange(
                    "name", event
                )}
            />
        )
        amountContent = (
            <Fragment>
                <div className="amount-dollar-sign">
                    $
                </div>
                <input
                    type="text"
                    className="common-form-input"
                    value={props.invoiceLine.amount}
                    onChange={(event) => props.handleInvoiceLineTextFieldChange(
                        "amount", event
                    )}
                    onBlur={() => props.forceInvoiceLineValueToFloat('amount')}
                />
            </Fragment>
        )
    } else {
        nameContent = props.invoiceLine.name;
        amountContent = StringUtility.stringNumberToDisplayCurrency('' + props.invoiceLine.amount);
    }

    return (
        <div
            id={`stripeInvoiceLine-${props.sequenceNumber}`}
            className="common-form-list-row"
        >
            <div className="common-form-list-row-content">
                <div className="common-form-list-row-cell number">
                    {props.sequenceNumber}
                </div>

                <div
                    className={props.getFormFieldClasses(
                        "common-form-list-row-cell name",
                        getInvoiceLineFieldId('stripeInvoiceItems', "name", props.index)
                    )}
                >
                    {nameContent}
                </div>

                <div
                    className={props.getFormFieldClasses(
                        "common-form-list-row-cell amount",
                        getInvoiceLineFieldId('stripeInvoiceItems', "amount", props.index)
                    )}
                >
                    {amountContent}
                </div>

                <ConditionalRenderer if={props.allowEdits} renderer={() => (
                    <div className="common-form-list-row-cell delete-button">
                        <div
                            className="invoice-line-delete-button"
                            title="Delete"
                            onClick={props.deleteInvoiceLineItem}
                        >
                            <img
                                src="/static/images/close-textcolor.svg"
                                className="invoice-line-delete-button-icon"
                            />
                        </div>
                    </div>
                )}/>
            </div>
        </div>
    )
}
